import { useState } from "react";
import {
  EditOutlined,
  LeftCircleOutlined,
  LoadingOutlined,
  PlusCircleOutlined
} from "@ant-design/icons";
import { Button, Col, Descriptions, Row, Table, Tag, notification, Typography } from "antd";
import { ColumnsType } from "antd/es/table";
import { useGetFormSectionQuery } from "../../redux/services/formSectionApi";
import {
  useUpdateFormQuestionMutation,
  useCreateFormQuestionMutation,
  useGetFormQuestionsQuery
} from "../../redux/services/formQuestionApi";
import AddFormQuestionInFormSectionModal from "./AddFormQuestionInFormSectionModal";
import ErrorDetails from "../../components/ErrorDetails";

interface ViewFormSectionProps {
  onGoBack: () => void;
  formSectionId: any;
}
const { Text } = Typography;
const ViewFormSection = ({ onGoBack, formSectionId }: ViewFormSectionProps) => {
  const [api, contextHolder] = notification.useNotification();
  const [showModal, setShowModal] = useState(false);
  const [selectedFormQuestion, setSelectedFormQuestion] = useState({});
  const [isEditMode, setEditMode] = useState(false);
  const getSectionResponse = useGetFormSectionQuery(formSectionId);
  const formSection = getSectionResponse?.data?.form_section;

  const { data, refetch, isLoading } = useGetFormQuestionsQuery({
    section_id: formSectionId,
    include_all: true
  });
  const formQuestions: any = data?.form_questions || [];

  const [createFormQuestion] = useCreateFormQuestionMutation();

  const [updateFormQuestion] = useUpdateFormQuestionMutation();

  const onUpdateFormQuestion = async (values: object, setLoading: any, form: any, id: any) => {
    try {
      setLoading(true);
      await updateFormQuestion({ id, formQuestion: values }).unwrap();
      api.success({
        message: "Update question successful",
        placement: "top"
      });
      form.resetFields();
      setLoading(false);
      setShowModal(false);
      setEditMode(false);

      refetch();
    } catch (error: any) {
      api.info({
        message: "Update question failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setLoading(false);
    }
  };

  const onUpdate = (formQuestion: any) => {
    setSelectedFormQuestion(formQuestion);
    setShowModal(true);
    setEditMode(true);
  };

  const columns: ColumnsType<any> = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title"
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description"
    },
    {
      title: "Type",
      dataIndex: "question_type",
      key: "question_type"
    },
    {
      title: "Is Mandatory",
      dataIndex: "mandatory",
      key: "mandatory",
      render: (mandatory) => (mandatory ? "mandatory" : "optional")
    },
    {
      title: "Choices",
      key: "choices",
      dataIndex: "choices",
      render: (choices: any[]) => (
        <>
          {choices?.map((choice, i) => (
            <div key={i}>
              <Tag>
                <Text>{choice}</Text>
              </Tag>
            </div>
          ))}
        </>
      )
    },
    {
      title: "Image Upload",
      dataIndex: "image_upload",
      key: "image_upload"
    },
    {
      title: "Video Upload",
      dataIndex: "video_upload",
      key: "video_upload"
    },
    {
      title: "Brands",
      key: "question_brands",
      dataIndex: "question_brands",
      render: (questionBrands: any[]) => (
        <>
          {questionBrands?.map((questionBrand) => (
            <div key={questionBrand?.name}>
              <Tag>
                <Text>
                  {questionBrand?.brand.name.toUpperCase()} - {questionBrand?.brand.code}
                </Text>
              </Tag>
            </div>
          ))}
        </>
      )
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "status",
      render: (tag) => (
        <Tag color={tag === "active" ? "green" : "red"} key={tag}>
          {tag.toUpperCase()}
        </Tag>
      )
    },
    {
      title: "Position",
      dataIndex: "position",
      key: "position"
    },
    {
      title: "Action",
      key: "action",
      render: (_, formQuestion) => (
        <Button icon={<EditOutlined />} onClick={() => onUpdate(formQuestion)}>
          Update
        </Button>
      )
    }
  ];

  const addFormQuestion = async (values: object, setLoading: any, form: any) => {
    try {
      setLoading(true);
      await createFormQuestion(values).unwrap();
      api.success({
        message: "Create question successful",
        placement: "top"
      });
      form.resetFields();
      setLoading(false);
      setShowModal(false);
      refetch();
    } catch (error: any) {
      api.info({
        message: "Create question failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setLoading(false);
    }
  };

  return (
    <>
      {contextHolder}
      {!isLoading && formSection && (
        <>
          <Row style={{ marginBottom: 20 }}>
            <Col
              sm={24}
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
              <h2>Section Details</h2>
              <Button onClick={onGoBack} size="middle" type="default" icon={<LeftCircleOutlined />}>
                Back
              </Button>
            </Col>
          </Row>
          <Descriptions
            title=""
            bordered
            column={1}
            labelStyle={{ fontWeight: 700, width: 300 }}
            size="middle"
          >
            <Descriptions.Item label="Name">{formSection?.name}</Descriptions.Item>
            <Descriptions.Item label="Section Type">{formSection?.section_type}</Descriptions.Item>
            <Descriptions.Item label="Brands">
              {formSection.section_brands?.map((sectionBrand: any) => (
                <div key={sectionBrand?.name}>
                  <Tag>
                    <Text>
                      {sectionBrand?.brand.name.toUpperCase()} - {sectionBrand?.brand.code}
                    </Text>
                  </Tag>
                </div>
              ))}
            </Descriptions.Item>
            <Descriptions.Item label="Position">{formSection?.position}</Descriptions.Item>
            <Descriptions.Item label="Status">{formSection?.status}</Descriptions.Item>
            <Descriptions.Item label="Use in installation">
              {formSection?.use_in_installation ? "Yes" : "No"}
            </Descriptions.Item>
            <Descriptions.Item label="Use in survey">
              {formSection?.use_in_survey ? "Yes" : "No"}
            </Descriptions.Item>
            <Descriptions.Item label="Use in visit">
              {formSection?.use_in_visit ? "Yes" : "No"}
            </Descriptions.Item>
          </Descriptions>

          <Row style={{ marginTop: 20, marginBottom: 20 }}>
            <Col
              sm={24}
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
              <h2>Questions</h2>
              <Button
                size="middle"
                onClick={() => setShowModal(!showModal)}
                icon={<PlusCircleOutlined />}
                type="default"
              >
                Add Question
              </Button>
            </Col>
          </Row>
          <Table
            rowKey="id"
            bordered
            columns={columns}
            pagination={false}
            dataSource={formQuestions}
            scroll={{ x: 400 }}
          />
          {showModal && (
            <AddFormQuestionInFormSectionModal
              isModalOpen={true}
              formSection={formSection}
              onCancel={() => {
                setShowModal(false);
                setEditMode(false);
              }}
              onUpdateFormQuestion={isEditMode && onUpdateFormQuestion}
              isEditMode={isEditMode}
              selectedFormQuestion={isEditMode ? selectedFormQuestion : {}}
              addFormQuestion={!isEditMode && addFormQuestion}
            />
          )}
        </>
      )}
      {isLoading && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh"
          }}
        >
          <LoadingOutlined style={{ fontSize: 24 }} spin />
        </div>
      )}
    </>
  );
};

export default ViewFormSection;
